import { useCallback, useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { useAppDispatch } from '../../../app/redux/hooks';
import LoadingModal from '../../../common/components/loading-modal/LoadingModal';
import logger from '../../../common/logger/AppLogger';
import {
  useGetLayoutDefinitionsQuery,
  useGetMenuCurrentVersionQuery,
  useGetMessagePresetsQuery,
  useGetVoidReasonsQuery,
  useOperatorSignOffMutation,
  useGetUnitOfSalesQuery,
  useCreateTransactionMutation,
  useLazyGetTransactionForOperatorQuery,
  useGetMenuCategoriesQuery,
  useGetPackagesQuery,
  useLazyGetBookingByIdQuery,
  useGetStockLevelsQuery,
  useLazyGetCardRequestQuery,
} from '../../api';
import {
  clearOperatorSession,
  useLayoutVersionLastPoll,
  useMenuVersionLastPoll,
  useOperatorSession,
  usePackageVersionLastPoll,
  useWorkstation,
} from '../../appState';
import { showErrorDialog } from '../../error-dialog/errorDialogSlice';
import {
  clearOrderState,
  setLayoutDefinitions,
  setMenuItemCategories,
  setMenuVersion,
  setMessagePresets,
  setPackages,
  setStockLevels,
  setTransaction,
  setTransactionFetching,
  setUnitOfSales,
  setVoidReasons,
  useDisableSignOff,
  useMenuVersionNumber,
  useTransaction,
  useUnitOfSales,
} from '../orderSlice';

import EndGameModal from './game/EndGameModal';
import { hasOpenGameLineItem } from './game/openGameLineItems';
import { Header } from './Header';
import LoyaltyScanInline from './LoyaltyScanInline';
import MenuSection from './MenuSection';
import OrderAddToTransaction from './OrderAddToTransaction';
import OrderSection from './OrderSection';

import '../styles.sass';

function Order() {
  const navigate = useNavigate();
  const { state: locationState } = useLocation();
  const operatorSession = useOperatorSession();
  const transaction = useTransaction();
  const dispatch = useAppDispatch();
  const workstation = useWorkstation();
  // Arm the end-game modal only if an outstanding game was present when a transaction was first
  // opened. If the operator adds a new game during this session, we don't want the modal to pop.
  const [armedForTxId, setArmedForTxId] = useState<number | null>(null);
  const lastSeenTxId = useRef<number | null>(null);

  const hasFobReader = workstation?.hasFobReader;

  const layoutLastPoll = useLayoutVersionLastPoll();
  const menuLastPoll = useMenuVersionLastPoll();
  const packageLastPoll = usePackageVersionLastPoll();
  const disableSignOff = useDisableSignOff();

  const [triggerSignOff] = useOperatorSignOffMutation();
  const {
    data: layoutDefinitions,
    isLoading: isLayoutDefinitionsLoading,
    refetch: refetchLayoutDefinitions,
  } = useGetLayoutDefinitionsQuery();
  const { data: menuItemCategories, isLoading: isMenuItemCategoriesLoading } = useGetMenuCategoriesQuery();
  const { data: unitOfSales, isLoading: isUnitOfSalesLoading } = useGetUnitOfSalesQuery();
  const { data: messagePresets, isLoading: messagePresetsLoading } = useGetMessagePresetsQuery();
  const { data: voidReasons, isLoading: voidReasonsLoading } = useGetVoidReasonsQuery();
  const { data: menuVersion, isLoading: menuVersionLoading, refetch: refetchMenu } = useGetMenuCurrentVersionQuery();
  const { data: packages, isLoading: packagesLoading, refetch: refetchPackages } = useGetPackagesQuery();
  const { data: stockLevels, isLoading: stockLevelsLoading } = useGetStockLevelsQuery();
  const [getBooking] = useLazyGetBookingByIdQuery();
  const [getCardRequest] = useLazyGetCardRequestQuery();
  const stateUnitOfSales = useUnitOfSales();
  const menuVersionNumber = useMenuVersionNumber();

  const [triggerGetTransactionForOperator, { data: transactionForOperator, error: transactionForOperatorError }] =
    useLazyGetTransactionForOperatorQuery();

  const [createTransaction] = useCreateTransactionMutation();
  const [isFetchingCardRequest, setIsFetchingCardRequest] = useState(false);

  const isLoading =
    isLayoutDefinitionsLoading ||
    menuVersionLoading ||
    isMenuItemCategoriesLoading ||
    isUnitOfSalesLoading ||
    messagePresetsLoading ||
    voidReasonsLoading ||
    packagesLoading ||
    stockLevelsLoading ||
    transaction === undefined ||
    isFetchingCardRequest;

  const keyboardState = useRef<string>('');

  const signOff = useCallback(() => {
    if (operatorSession !== undefined) {
      triggerSignOff({
        operatorId: operatorSession.operator.id,
      })
        .unwrap()
        .then(() => {
          dispatch(clearOperatorSession());
          dispatch(clearOrderState());
          navigate('/');
        })
        .catch(() => {
          clearOperatorSession();
          navigate('/');
        });
    }
  }, [dispatch, navigate, operatorSession, triggerSignOff]);

  useEffect(() => {
    const currentTxId = transaction && transaction.length === 1 ? transaction[0].id : null;
    if (currentTxId !== lastSeenTxId.current) {
      lastSeenTxId.current = currentTxId;
      if (currentTxId != null && hasOpenGameLineItem(transaction[0].transactionLineItems)) setArmedForTxId(currentTxId);
      else setArmedForTxId(null);
    }
  }, [transaction]);

  useEffect(() => {
    if (layoutDefinitions) dispatch(setLayoutDefinitions(layoutDefinitions));
  }, [layoutDefinitions, dispatch, layoutLastPoll]);

  // When the polled version and redux version is different, let's get the latest layout version
  useEffect(() => {
    if (layoutDefinitions && layoutDefinitions.version !== layoutLastPoll) refetchLayoutDefinitions();
  }, [layoutDefinitions, layoutLastPoll, refetchLayoutDefinitions]);

  useEffect(() => {
    if (voidReasons) dispatch(setVoidReasons(voidReasons));
  }, [voidReasons, dispatch]);

  useEffect(() => {
    if (stockLevels) dispatch(setStockLevels(stockLevels));
  }, [stockLevels, dispatch]);

  useEffect(() => {
    if (packages) dispatch(setPackages(packages));
  }, [packages, dispatch]);

  // When the polled version and redux version is different, let's get the latest package version
  useEffect(() => {
    if (packages && packages.version !== packageLastPoll) refetchPackages();
  }, [packageLastPoll, packages, refetchPackages]);

  useEffect(() => {
    if (messagePresets) dispatch(setMessagePresets(messagePresets));
  }, [messagePresets, dispatch]);

  useEffect(() => {
    if (menuItemCategories) dispatch(setMenuItemCategories(menuItemCategories));
  }, [menuItemCategories, dispatch]);

  useEffect(() => {
    if (unitOfSales && stateUnitOfSales.length === 0) dispatch(setUnitOfSales(unitOfSales));
  }, [unitOfSales, dispatch, stateUnitOfSales.length]);

  useEffect(() => {
    console.warn('MENU VERSION', menuVersion, menuVersionNumber);
    if (menuVersion && menuVersion.versionNumber > menuVersionNumber) dispatch(setMenuVersion(menuVersion));
  }, [menuVersion, menuVersionNumber, dispatch]);

  // Check the polled menu date and our menu date, refetch
  useEffect(() => {
    if (menuVersion && menuVersion.versionNumber !== menuLastPoll) refetchMenu();
  }, [menuLastPoll, menuVersion, refetchMenu]);

  useEffect(() => {
    if (transaction && transaction.length > 0) {
      // Check for timed out
      if (transaction.some((p) => p.timedOutCardRequestId != null)) {
        const timedOut = transaction.find((p) => p.timedOutCardRequestId != null);
        if (timedOut && timedOut.timedOutCardRequestId) {
          // Check if we've already handled this card request ID
          logger.info(`Transaction ${timedOut.id} has timed out card request ${timedOut.timedOutCardRequestId}, clearing flag`);

          // Show loading spinner while fetching card request details
          setIsFetchingCardRequest(true);

          // Fetch card request details to get accurate amount and terminal info
          getCardRequest(timedOut.timedOutCardRequestId)
            .unwrap()
            .then((cardRequest) => {
              setIsFetchingCardRequest(false);
              const transactionNumber = timedOut.transactionNumber;
              const sequenceNumber = timedOut.sequenceNumber;
              const operatorName = operatorSession ? operatorSession.operator.firstName : 'Unknown';
              const amount = cardRequest.amount;
              const reason = 'TimedOut';
              const pedName = cardRequest.terminalName ?? 'Unknown';
              const pedTerminalId = cardRequest.terminalId ?? 'Unknown';

              logger.info('[Order.tsx] About to show timeout dialog for card request', {
                cardRequestId: timedOut.timedOutCardRequestId,
                amount,
                pedName,
                pedTerminalId,
                hasSourceField: true,
              });

              const priceFormatter = new Intl.NumberFormat('en-GB', {
                style: 'currency',
                currency: 'GBP',
              });

              dispatch(
                showErrorDialog({
                  errorType: 'card-payment-failure',
                  message: `Card payment failure for ${transactionNumber}/${sequenceNumber}.<br><br> Operator was <b>${operatorName}</b><br>Value <b>${priceFormatter.format(
                    amount,
                  )}</b><br> Card Machine was <b>${pedName}</b><br><br>Card Result was ${reason}<br><br>Communication with the PDQ has failed, please check card receipt and record accordingly`,
                  dismissible: true,
                  buttonText: 'Payment Failed',
                  backgroundColor: 'red',
                  alternateButtonText: 'Payment Succeeded',
                  showAlternateButton: true,
                  requireConfirmationOnClose: true,
                  confirmNoText: 'Back',
                  confirmYesText: 'Confirm Failed',
                  confirmAlternateNoText: 'Back',
                  confirmAlternateYesText: 'Enter Auth Code',
                  confirmationText: `Are you sure the payment failed? Please confirm you checked the ${pedName} card machine and have proof the payment declined for ${priceFormatter.format(
                    amount,
                  )}`,
                  confirmationTextAlternate: `Please confirm the payment succeeded,<br /><br /> You will need to enter the Auth Code off the card machine.<br /><br /><b>Amount:</b> ${priceFormatter.format(
                    amount,
                  )}<br /><b>Card Machine:</b> ${pedName}`,
                  state: {
                    transactionId: timedOut.id,
                    transactionNumber,
                    sequenceNumber,
                    operatorName,
                    amount,
                    reason,
                    cardRequestId: timedOut.timedOutCardRequestId,
                    pedName,
                    pedTerminalId,
                    message: `Card payment failure for ${transactionNumber}/${sequenceNumber}.<br><br> Operator was <b>${operatorName}</b><br>Value <b>${priceFormatter.format(
                      amount,
                    )}</b><br> Card Machine was <b>${pedName}</b><br><br>Card Result was ${reason}<br><br>Communication with the PDQ has failed, please check card receipt and record accordingly`,
                    type: 'card-failure',
                    source: 'timeout-detection',
                  },
                  alternateState: {
                    transactionId: timedOut.id,
                    transactionNumber,
                    sequenceNumber,
                    operatorName,
                    amount,
                    reason,
                    cardRequestId: timedOut.timedOutCardRequestId,
                    pedName,
                    pedTerminalId,
                    message: `Card payment failure for ${transactionNumber}/${sequenceNumber}.<br><br> Operator was <b>${operatorName}</b><br>Value <b>${priceFormatter.format(
                      amount,
                    )}</b><br> Card Machine was <b>${pedName}</b><br><br>Card Result was ${reason}<br><br>Communication with the PDQ has failed, please check card receipt and record accordingly`,
                    type: 'offline-card',
                    source: 'timeout-detection',
                  },
                }),
              );
            })
            .catch((error) => {
              setIsFetchingCardRequest(false);
              logger.error(`Failed to fetch card request ${timedOut.timedOutCardRequestId}`, error);
              // Fallback to using transaction data if API call fails
              const transactionNumber = timedOut.transactionNumber;
              const sequenceNumber = timedOut.sequenceNumber;
              const operatorName = operatorSession ? operatorSession.operator.firstName : 'Unknown';
              const amount = timedOut.totalDue;
              const reason = 'TimedOut';
              const pedName = timedOut.pinEntryDeviceTerminalId ?? 'Unknown';

              const priceFormatter = new Intl.NumberFormat('en-GB', {
                style: 'currency',
                currency: 'GBP',
              });

              dispatch(
                showErrorDialog({
                  errorType: 'card-payment-failure',
                  message: `Card payment failure for ${transactionNumber}/${sequenceNumber}.<br><br> Operator was <b>${operatorName}</b><br>Value <b>${priceFormatter.format(
                    amount,
                  )}</b><br> Card Machine was <b>${pedName}</b><br><br>Card Result was ${reason}<br><br>Communication with the PDQ has failed, please check card receipt and record accordingly`,
                  dismissible: true,
                  buttonText: 'Payment Failed',
                  backgroundColor: 'red',
                  alternateButtonText: 'Payment Succeeded',
                  showAlternateButton: true,
                  requireConfirmationOnClose: true,
                  confirmNoText: 'Back',
                  confirmYesText: 'Confirm Failed',
                  confirmAlternateNoText: 'Back',
                  confirmAlternateYesText: 'Enter Auth Code',
                  confirmationText: `Are you sure the payment failed? Please confirm you checked the ${pedName} card machine and have proof the payment declined for ${priceFormatter.format(
                    amount,
                  )}`,
                  confirmationTextAlternate: `Please confirm the payment succeeded,<br /><br /> You will need to enter the Auth Code off the card machine.<br /><br /><b>Amount:</b> ${priceFormatter.format(
                    amount,
                  )}<br /><b>Card Machine:</b> ${pedName}`,
                  state: {
                    transactionId: timedOut.id,
                    transactionNumber,
                    sequenceNumber,
                    operatorName,
                    amount,
                    reason,
                    cardRequestId: timedOut.timedOutCardRequestId,
                    pedName,
                    message: `Card payment failure for ${transactionNumber}/${sequenceNumber}.<br><br> Operator was <b>${operatorName}</b><br>Value <b>${priceFormatter.format(
                      amount,
                    )}</b><br> Card Machine was <b>${pedName}</b><br><br>Card Result was ${reason}<br><br>Communication with the PDQ has failed, please check card receipt and record accordingly`,
                    type: 'card-failure',
                    source: 'timeout-detection',
                  },
                  alternateState: {
                    transactionId: timedOut.id,
                    transactionNumber,
                    sequenceNumber,
                    operatorName,
                    amount,
                    reason,
                    cardRequestId: timedOut.timedOutCardRequestId,
                    pedName,
                    message: `Card payment failure for ${transactionNumber}/${sequenceNumber}.<br><br> Operator was <b>${operatorName}</b><br>Value <b>${priceFormatter.format(
                      amount,
                    )}</b><br> Card Machine was <b>${pedName}</b><br><br>Card Result was ${reason}<br><br>Communication with the PDQ has failed, please check card receipt and record accordingly`,
                    type: 'offline-card',
                    source: 'timeout-detection',
                  },
                }),
              );
            });
        }
      }
    }
  }, [dispatch, operatorSession, transaction, getCardRequest]);

  useEffect(() => {
    if (disableSignOff || hasFobReader === false) return;
    const eventHandler = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        const regexSignOff = /^0{3,4}(00)$/gm;
        if (operatorSession === undefined) navigate('/');
        else {
          const regexSignOffMatch = keyboardState.current.match(regexSignOff);
          if (regexSignOffMatch !== null) {
            logger.info('Sign Off needed');
            signOff();
          }
        }

        keyboardState.current = '';
      } else {
        if (e.key.length === 1) {
          if (keyboardState.current.length >= 16) keyboardState.current = keyboardState.current.substring(1) + e.key;
          else keyboardState.current = keyboardState.current + e.key;
        }
      }
    };
    window.addEventListener('keydown', eventHandler);
    return () => window.removeEventListener('keydown', eventHandler);
  }, [operatorSession, signOff, dispatch, navigate, disableSignOff, hasFobReader]);

  useEffect(() => {
    if (operatorSession === undefined) navigate('/');
    else {
      dispatch(setTransactionFetching(true));
      triggerGetTransactionForOperator(operatorSession.operator.id);
    }
  }, [operatorSession, navigate, triggerGetTransactionForOperator, dispatch]);

  useEffect(() => {
    if (transactionForOperatorError !== undefined) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const errorGeneric = transactionForOperatorError as any;
      if (errorGeneric.status && errorGeneric.status === 404 && operatorSession) {
        createTransaction(operatorSession.operator.id)
          .unwrap()
          .then((createdTransaction) => {
            dispatch(setTransaction([createdTransaction]));
          })
          .catch((err) => {
            if (err.status && err.status === 412) {
              dispatch(showErrorDialog({ message: err.data.detail, dismissible: true }));
              navigate('/');
            } else if (err.status && err.status === 406) {
              dispatch(showErrorDialog({ message: err.data.detail, dismissible: true }));
              navigate('/start-session');
            }
          });
      } else logger.error(`Error getting transaction for operator with error ${JSON.stringify(transactionForOperatorError)}`);
    }
  }, [transactionForOperatorError, createTransaction, dispatch, navigate, operatorSession]);

  useEffect(() => {
    if (transactionForOperator !== undefined) {
      dispatch(setTransaction(transactionForOperator));
      if (transactionForOperator.length > 1) navigate('../tender');
      if (transactionForOperator.length === 1) {
        if (transactionForOperator[0].bookingId && transactionForOperator[0].bookingPaid && transactionForOperator[0].bookingAmount)
          if (transactionForOperator[0].bookingPaid < transactionForOperator[0].bookingAmount) navigate('../tender');

        if (transactionForOperator[0].cardPaymentInProgress === true) navigate('../tender');
        if (transactionForOperator[0].forbidNewLineItems === true) navigate('../tender');
      }
    }
  }, [transactionForOperator, dispatch, navigate]);

  useEffect(() => {
    if (locationState && locationState.source === 'booking-redeem') {
      if (transaction && transaction.length === 1 && transaction[0].bookingId) {
        const bookingId = transaction[0].bookingId;
        getBooking(bookingId)
          .unwrap()
          .then((booking) => {
            if (booking.packages && booking.packages.length > 0) {
              // There are packages to redeem
              navigate('/order/booking-packages', {
                state: {
                  bookingPackages: booking.packages,
                  transactionId: transaction[0].id,
                },
              });
            }
          });
      }
    }
  }, [getBooking, locationState, navigate, transaction]);

  if (isLoading) return <LoadingModal />;

  return (
    <>
      <div className="pos-container pos-container_role_main">
        <LoyaltyScanInline />
        <Header />

        <div className="pos-main">
          <MenuSection />
          <OrderSection />
        </div>
      </div>
      <OrderAddToTransaction />
      {transaction &&
        transaction.length === 1 &&
        transaction[0].id === armedForTxId &&
        hasOpenGameLineItem(transaction[0].transactionLineItems) && (
          <EndGameModal transaction={transaction[0]} onSkip={() => setArmedForTxId(null)} onAllEnded={() => setArmedForTxId(null)} />
        )}
      <Outlet />
    </>
  );
}
export default Order;
